import type { V1AutomationSummary } from '@/generated/hooks/grimoire/models';

/**
 * Where an automation sits between being written and running. One automation id
 * can have a published and a draft version at once, so the catalog shows one
 * tile per automation with the draft as a state on it.
 */
export enum EAutomationLifecycle {
  /** Written but never published. There is nothing live to run. */
  UNPUBLISHED = 'unpublished',
  /** Published, and the working version matches it. Nothing in progress. */
  LIVE = 'live',
  /** Published, with a newer working version being edited. */
  LIVE_WITH_DRAFT = 'live-with-draft',
}

/** An automation's lifecycle state and the versions behind it. */
export interface IAutomationLifecycle {
  state: EAutomationLifecycle;
  /** The version currently running. Absent only when never published. */
  liveVersion?: string;
  /** The working version, when it is ahead of what is published. */
  draftVersion?: string;
}

/**
 * Derive the lifecycle from the list's version fields: no published version is
 * unpublished, a working version that differs from it is live with a draft,
 * otherwise live. Versions are compared as strings since only equality matters.
 *
 * @param summary - The automation's version fields
 * @returns Its lifecycle
 */
export function getAutomationLifecycle(
  summary: Pick<V1AutomationSummary, 'version' | 'latest_published_version'>,
): IAutomationLifecycle {
  const { version, latest_published_version: published } = summary;

  if (!published) {
    return {
      state: EAutomationLifecycle.UNPUBLISHED,
      draftVersion: version || undefined,
    };
  }

  if (version && version !== published) {
    return {
      state: EAutomationLifecycle.LIVE_WITH_DRAFT,
      liveVersion: published,
      draftVersion: version,
    };
  }

  return { state: EAutomationLifecycle.LIVE, liveVersion: published };
}

/** True when the automation has a version a person is still working on. */
export function hasActiveDraft(
  summary: Pick<V1AutomationSummary, 'version' | 'latest_published_version'>,
): boolean {
  const { state } = getAutomationLifecycle(summary);
  return (
    state === EAutomationLifecycle.UNPUBLISHED ||
    state === EAutomationLifecycle.LIVE_WITH_DRAFT
  );
}

/** True when something is published and therefore actually runnable. */
export function isLive(
  summary: Pick<V1AutomationSummary, 'version' | 'latest_published_version'>,
): boolean {
  return (
    getAutomationLifecycle(summary).state !== EAutomationLifecycle.UNPUBLISHED
  );
}
